import { motion, AnimatePresence } from 'framer-motion'
import { X, ExternalLink, Github, Calendar, User, CheckCircle, Clock, Zap, TrendingUp } from 'lucide-react'
import { useEffect } from 'react'

const ProjectModal = ({ project, isOpen, onClose, language }) => {
  const description = project?.description?.[language] || project?.description?.sq
  const features = project?.features?.[language] || project?.features?.sq || []
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])
  
  if (!project) return null
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'live': return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      case 'in-progress': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }
  
  const getStatusIcon = (status) => {
    switch(status) {
      case 'live': return <CheckCircle className="w-4 h-4" />
      case 'completed': return <CheckCircle className="w-4 h-4" />
      case 'in-progress': return <Clock className="w-4 h-4" />
      default: return null
    }
  }
  
  const getStatusLabel = (status) => {
    const labels = {
      live: { sq: 'Live & Aktiv', en: 'Live & Active' },
      completed: { sq: 'E Kompletuar', en: 'Completed' },
      'in-progress': { sq: 'Në Progres', en: 'In Progress' }
    }
    return labels[status]?.[language] || status
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-dark/95 backdrop-blur-md z-[100]"
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-[101] overflow-y-auto">
            <div className="min-h-screen px-4 py-8 flex items-center justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-5xl bg-gradient-to-br from-bronze-900/95 to-dark/95 backdrop-blur-2xl rounded-3xl border border-gold-400/30 shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-6 right-6 z-10 p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all hover:rotate-90 duration-300"
                >
                  <X className="w-6 h-6" />
                </button>
                
                {/* Header Image */}
                <div className="relative h-80 bg-gradient-to-br from-bronze-800 to-dark overflow-hidden">
                  {project.thumbnail ? (
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-9xl font-display text-gold-400/20">
                        {project.title.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-bronze-900 via-bronze-900/50 to-transparent" />
                  
                  {/* Featured Badge */}
                  {project.featured && (
                    <div className="absolute top-6 left-6 bg-gradient-gold px-4 py-2 rounded-full text-sm font-bold text-white shadow-lg">
                      ⭐ Featured Project
                    </div>
                  )}
                  
                  {/* Title Overlay */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <h2 className="text-4xl font-display font-bold text-white mb-3">
                      {project.title}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className={`px-4 py-2 rounded-full text-sm font-semibold border flex items-center gap-2 backdrop-blur-xl ${getStatusColor(project.status)}`}>
                        {getStatusIcon(project.status)}
                        {getStatusLabel(project.status)}
                      </div>
                      <div className="px-4 py-2 bg-white/10 backdrop-blur-xl text-white rounded-full text-sm font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {project.year}
                      </div>
                      {project.client && (
                        <div className="px-4 py-2 bg-white/10 backdrop-blur-xl text-white rounded-full text-sm font-medium flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {project.client}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-8 max-h-[60vh] overflow-y-auto custom-scrollbar">
                  <div className="grid lg:grid-cols-3 gap-8">
                    {/* Main Content - Left Side */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Description */}
                      <div>
                        <h3 className="text-xl font-bold text-gold-400 mb-3 flex items-center gap-2">
                          <span className="text-2xl">📋</span>
                          {language === 'sq' ? 'Përshkrimi' : 'Description'}
                        </h3>
                        <p className="text-beige-200 leading-relaxed">
                          {description}
                        </p>
                      </div>
                      
                      {/* Features */}
                      {features.length > 0 && (
                        <div>
                          <h3 className="text-xl font-bold text-gold-400 mb-4 flex items-center gap-2">
                            <Zap className="w-5 h-5" />
                            {language === 'sq' ? 'Veçoritë Kryesore' : 'Key Features'}
                          </h3>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {features.map((feature, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="flex items-start gap-2 text-beige-200"
                              >
                                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                <span className="text-sm">{feature}</span>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Screenshots Gallery */}
                      {project.screenshots && project.screenshots.length > 0 && (
                        <div>
                          <h3 className="text-xl font-bold text-gold-400 mb-4">
                            📸 {language === 'sq' ? 'Screenshots' : 'Screenshots'}
                          </h3>
                          <div className="grid sm:grid-cols-2 gap-4">
                            {project.screenshots.map((screenshot, index) => (
                              <motion.div
                                key={index}
                                whileHover={{ scale: 1.05 }}
                                className="relative aspect-video rounded-xl overflow-hidden border border-gold-400/20 cursor-pointer"
                              >
                                <img
                                  src={screenshot}
                                  alt={`Screenshot ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Sidebar - Right Side */}
                    <div className="space-y-6">
                      {/* Action Buttons */}
                      <div className="space-y-3">
                        {project.liveUrl && (
                          <a
                            href={project.liveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-gold text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-gold-400/50 transition-all"
                          >
                            <ExternalLink className="w-5 h-5" />
                            {language === 'sq' ? 'Shiko Live' : 'View Live'}
                          </a>
                        )}
                        {project.githubUrl && (
                          <a
                            href={project.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-all"
                          >
                            <Github className="w-5 h-5" />
                            {language === 'sq' ? 'Shiko Kodin' : 'View Code'}
                          </a>
                        )}
                      </div>
                      
                      {/* Technologies */}
                      <div>
                        <h3 className="text-lg font-bold text-gold-400 mb-3 flex items-center gap-2">
                          <span className="text-xl">⚙️</span>
                          {language === 'sq' ? 'Teknologjitë' : 'Technologies'}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {project.technologies.map((tech, index) => (
                            <span
                              key={index}
                              className="px-3 py-1.5 bg-gold-400/10 text-gold-400 text-sm font-medium rounded-lg border border-gold-400/20"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Color Palette */}
                      <div>
                        <h3 className="text-lg font-bold text-gold-400 mb-3 flex items-center gap-2">
                          <span className="text-xl">🎨</span>
                          {language === 'sq' ? 'Ngjyrat' : 'Colors'}
                        </h3>
                        <div className="flex flex-wrap gap-3">
                          {project.colors.map((color, index) => (
                            <div key={index} className="text-center">
                              <div
                                className="w-12 h-12 rounded-xl border-2 border-white/20 shadow-lg mb-1"
                                style={{ backgroundColor: color }}
                              />
                              <span className="text-xs text-beige-300 font-mono">
                                {color}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Performance Stats */}
                      {project.stats && (
                        <div>
                          <h3 className="text-lg font-bold text-gold-400 mb-3 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" />
                            {language === 'sq' ? 'Performance' : 'Performance'}
                          </h3>
                          <div className="space-y-3">
                            {Object.entries(project.stats).map(([key, value]) => (
                              <div key={key}>
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-beige-300 capitalize">{key}</span>
                                  <span className="text-gold-400 font-semibold">{value}</span>
                                </div>
                                <div className="h-2 bg-bronze-800 rounded-full overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${value}${typeof value === 'number' ? '%' : ''}` }}
                                    transition={{ delay: 0.3, duration: 1 }}
                                    className="h-full bg-gradient-gold"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default ProjectModal
